/**
 * When this browser last changed a person, so the register can catch up with its own writes.
 *
 * **The register is fed by a projection that is a second or two behind, and AD-23's 503 does not
 * cover that.** The indexer polls the feed on AD-22's two seconds; the 503 is raised only while the
 * projection is still BUILDING, never while it is merely BEHIND. So a search made moments after a
 * registration is answered — truthfully — with no matches, and a record just erased is still listed.
 * Neither answer is wrong when it is given; both are wrong a second later, and nothing asked again:
 * the term is debounced into a single request, `staleTime` is fifteen seconds and no person write
 * invalidates anything. An operator who registered somebody, went back to the list and searched for
 * them was told they were not registered, and stayed told for as long as the screen was open. That
 * is the duplicate-record cascade AD-22 and AD-23 exist to prevent, arriving through the one door
 * they left open.
 *
 * **The window is keyed on the WRITE rather than on the answer, and the difference is the whole
 * point.** Re-asking every empty answer would put three extra requests behind every search for
 * somebody who genuinely is not registered — the commonest search there is — against the coarse
 * per-address tier that a gate's own traffic shares. Keyed on the write,
 * the cost falls only on the few seconds after this browser changed something, which is exactly
 * when it knows an answer may be stale, and it covers a removal as well as an arrival: the case
 * that a person should have DISAPPEARED cannot be detected by looking at the answer at all.
 *
 * A module-level instant rather than state, because it is a fact about this tab rather than about
 * any component: the screen that writes unmounts on its way to the record, and the screen that
 * needs to know is mounted afterwards.
 */

/** How long after a write the register keeps asking. Comfortably past the indexer's own interval. */
export const PROJECTION_CATCH_UP_MS = 5_000;

/** How often it asks inside that window. */
export const PROJECTION_CATCH_UP_POLL_MS = 1_000;

let lastPersonWriteAt = Number.NEGATIVE_INFINITY;

/** Records that this browser created, amended or erased a person. */
export function notePersonWrite(at: number = Date.now()): void {
  lastPersonWriteAt = at;
}

/**
 * Whether a person the register is being asked about may not have reached the projection yet.
 *
 * Written as a function of `now` so the rule has a test that does not need a clock: the boundary is
 * the thing worth pinning, and an interval callback is a poor place to discover it is off by one.
 */
export function withinProjectionCatchUp(now: number = Date.now()): boolean {
  return now - lastPersonWriteAt < PROJECTION_CATCH_UP_MS;
}

/** Test seam: forgets the last write, so one case cannot leak its clock into the next. */
export function forgetPersonWrites(): void {
  lastPersonWriteAt = Number.NEGATIVE_INFINITY;
}
